import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../components/AdminLayout";
import { listAdminUsers, type AdminUser } from "@/lib/admin";
import { UserRole } from "@/contexts/AuthContext";

function roleBadge(role: UserRole, t: (k: string) => string): { label: string; cls: string } {
  if (role === UserRole.Provider) return { label: t("admin.users.provider"), cls: "bg-ocean/10 text-ocean" };
  if (role === UserRole.Admin) return { label: t("admin.users.admin"), cls: "bg-coral/10 text-coral" };
  return { label: t("admin.users.tourist"), cls: "bg-turquoise/10 text-turquoise" };
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("es-MX");
}

export default function AdminUsuariosPage() {
  const { t } = useTranslation("admin");
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listAdminUsers({ search: search.trim() || undefined })
      .then((data) => { if (!cancelled) setUsers(data); })
      .catch(() => { if (!cancelled) setUsers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search]);

  return (
    <AdminLayout title={t("admin.users.title")}>
      <div className="mb-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("admin.users.search")}
          className="w-full sm:w-80 border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-ocean"
        />
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.users.name")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.users.email")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.users.role")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.users.bookings")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.users.marketing")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3">{t("admin.users.joined")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-10 text-center text-gray-400">{t("admin.loading")}</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="py-10 text-center text-gray-400">{t("admin.users.empty")}</td></tr>
            ) : (
              users.map((u) => {
                const badge = roleBadge(u.role, t);
                return (
                  <tr key={u.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-4 font-medium text-charcoal">{u.fullName}</td>
                    <td className="py-3 pr-4 text-gray-600">{u.email}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="py-3 pr-4 text-gray-600">{u.bookingsCount}</td>
                    <td className="py-3 pr-4">
                      {u.acceptsMarketing ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-ocean">
                          <i className="ri-check-line" /> {t("admin.users.marketingYes")}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">{t("admin.users.marketingNo")}</span>
                      )}
                    </td>
                    <td className="py-3 text-gray-500">{formatDate(u.createdAt)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
